import type { CSSProperties } from "react";
import type { FpPageNumberFormat } from "@forgepoint/shared";
import { usePdfPageInfo } from "@/modules/publicQuote/components/PdfPageViewer";
import { counterMarginStyle } from "./pageCounterMargin";
import { useQuoteDocumentContext } from "./quoteDocumentContext";
import type { FpCommonProps } from "./types";

/**
 * Counter defaults matching the prose scale `<fp-text>` renders with: the same
 * line-height ratio, anchored to the top of the line box. Without these the
 * bare span inherits the page's larger line box and its glyphs sit visibly
 * lower than prose in a sibling cell. Authored `style` declarations override.
 */
export const PAGE_COUNTER_BASE_STYLE: CSSProperties = {
  lineHeight: 1.7142857,
  verticalAlign: "top",
};

interface FpPageNumberProps extends FpCommonProps {
  format: FpPageNumberFormat;
  /** The authored `margin` shorthand, or null when unset. */
  margin?: string | null;
}

/**
 * Renders `<fp-page-number>`: the current page number, offset past cover pages,
 * in the shape its `format` asks for.
 * @param props - The counter format, its margin, and common fp-* props
 * @returns A span containing the formatted page counter
 */
export function FpPageNumber({
  format,
  margin,
  className,
  style,
  "data-fp-node": dataFpNode,
}: FpPageNumberProps) {
  const { pageNumber, totalPages } = usePdfPageInfo();
  const { footerPageOffset } = useQuoteDocumentContext();
  return (
    <span
      className={className}
      style={{
        ...PAGE_COUNTER_BASE_STYLE,
        ...counterMarginStyle(margin),
        ...style,
      }}
      data-fp-node={dataFpNode}
    >
      {formatPageCounter(
        format,
        pageNumber - footerPageOffset,
        totalPages - footerPageOffset,
      )}
    </span>
  );
}

/**
 * Builds the counter's text for a format.
 * @param format - The counter shape
 * @param current - The offset current page
 * @param total - The offset total page count
 * @returns The text the counter prints
 */
function formatPageCounter(
  format: FpPageNumberFormat,
  current: number,
  total: number,
): string {
  if (format === "n-of-total") {
    return `${current}/${total}`;
  }
  if (format === "page-n-of-total") {
    return `Page ${current} of ${total}`;
  }
  return `${current}`;
}
